import './FilterOptionSwatchOptionItem.scss';

import { FilterOptionItem, useFilterOptionItem } from '@components/FilterOptionItem';
import FilterOptionItemButton from '@components/FilterOptionItem/Button';
import FilterOptionItemLabel from '@components/FilterOptionItem/Label';
import { FILTER_OPTION_ITEM_SWATCH_TYPE } from '@providers/FilterProvider/enum';
import type { Dict } from '@types';
import { createClsNameMap, formatLabelFilterOption, mapModifiers, stripHtml } from '@utils';
import { useMemo } from 'react';

export const clsNameMap = createClsNameMap({
  modifiers: ['disabled'],
  elements: {
    img: createClsNameMap({
      modifiers: ['selected', 'square', 'list'],
    }),
  },
})('filter-option-swatch-item');

export type FilterOptionSwatchItemProps = {
  filterOptionId: string;
  filterOptionLabel: string;
  value: string;
  docCount: string | number;
  option: Dict;
  fileBaseUrl: string;
  swatchSettings: Dict;
  itemSettings?: {
    displayAllValuesInUppercaseForm: boolean;
    selectType: string;
    filterType: string;
  };
};

type SwatchItemAttributes = {
  backgroundColor: string;
  backgroundImage: string;
};

const FilterOptionSwatchItem = ({
  filterOptionId,
  value,
  itemSettings,
  filterOptionLabel,
  option,
  docCount,
  fileBaseUrl,
  swatchSettings,
}: FilterOptionSwatchItemProps) => {
  const {
    isOptionItemSelected,
    handleOptionItemSelect,
    showOutOfStockOption,
    showFilterOptionCount,
  } = useFilterOptionItem({
    value,
    filterOptionId,
  });

  const attributes: SwatchItemAttributes = useMemo(() => {
    if (swatchSettings) {
      const swatchKeys = Object.keys(swatchSettings);
      let swatchSettingKey;

      if (swatchKeys.length > 0) {
        swatchSettingKey = swatchKeys.find(
          (key) =>
            key.replace('pfs-swatch-', '') === value ||
            key.replace('pfs-swatch-', '').toLowerCase() === value.toLowerCase()
        );
      }

      if (swatchSettingKey) {
        const swatchSetting = swatchSettings[swatchSettingKey];
        switch (swatchSetting.type) {
          case FILTER_OPTION_ITEM_SWATCH_TYPE.ONE_COLOR:
            if (swatchSetting.colorCodes.length > 0 && swatchSetting.colorCodes[0]) {
              return {
                backgroundImage: 'none',
                backgroundColor: swatchSetting.colorCodes[0],
              };
            }
            break;
          case FILTER_OPTION_ITEM_SWATCH_TYPE.TWO_COLORS:
            if (
              swatchSetting.colorCodes.length > 1 &&
              swatchSetting.colorCodes[0] &&
              swatchSetting.colorCodes[1]
            ) {
              return {
                backgroundColor: 'transparent',
                backgroundImage:
                  'linear-gradient(to top left, ' +
                  swatchSetting.colorCodes[1] +
                  ' 50%, ' +
                  swatchSetting.colorCodes[0] +
                  ' 50%)',
              };
            }
            break;
          case FILTER_OPTION_ITEM_SWATCH_TYPE.IMAGE:
            if (swatchSetting.imageUrl) {
              const swatchFileUrl = swatchSetting.imageUrl;

              return {
                backgroundColor: 'transparent',
                backgroundImage: 'url(' + swatchFileUrl + ')',
              };
            }
            break;
        }
      }
    }

    return {
      backgroundColor: value,
      backgroundImage: 'none',
    };
  }, [value, filterOptionLabel, fileBaseUrl]);

  const [swatchStyle, swatchType] = (option?.swatchStyle || '').split('-');

  const disabled = docCount === 0;

  if (!showOutOfStockOption && disabled) return null;

  if (!stripHtml(value)) return null;

  return (
    <FilterOptionItem className={mapModifiers(clsNameMap, { disabled })}>
      <FilterOptionItemButton
        as='button'
        isSelected={isOptionItemSelected}
        title={formatLabelFilterOption(value, option?.prefix || '')}
        docCount={docCount}
        showFilterOptionCount={swatchType === 'list' ? showFilterOptionCount : false}
        label={
          <>
            <span
              className={mapModifiers(clsNameMap.img, {
                selected: isOptionItemSelected,
                square: swatchStyle === 'square',
                list: swatchType === 'list',
              })}
              style={attributes}
            ></span>
            {swatchType === 'list' && (
              <FilterOptionItemLabel
                label={formatLabelFilterOption(value, option?.prefix || '')}
                settings={itemSettings}
              />
            )}
          </>
        }
        onClick={handleOptionItemSelect}
      />
    </FilterOptionItem>
  );
};

export default FilterOptionSwatchItem;
